<template>
  <div class="dma">
    <div v-if="!hasRows" class="dma-empty">{{ $t('surveyBuilder.dimitraModule') || 'Dimitra Module' }}</div>

    <div v-for="(rows, moduleName) in moduleEntries" :key="moduleName" class="dma-module mb-4">
      <div class="dma-module-heading mb-3">Dimitra {{ getDimitraModuleDisplayName(moduleName) }}</div>
      <div class="dma-cards">
        <div
          v-for="(row, rowIdx) in rows"
          :key="`${moduleName}-${row.id || rowIdx}`"
          class="dma-card d-flex flex-column pa-4"
        >
          <div class="dma-card-title mb-3">{{ getDimitraCardTitle(moduleName, row, rowIdx) }}</div>

          <div v-if="getDimitraPreviewFields(moduleName, row, rowIdx).length" class="dma-grid">
            <div
              v-for="field in getDimitraPreviewFields(moduleName, row, rowIdx)"
              :key="`${moduleName}-${row.id || rowIdx}-${field.key}`"
              class="dma-field"
            >
              <div class="dma-field-label">{{ field.label }}</div>
              <div class="dma-field-value">{{ field.value }}</div>
            </div>
          </div>
          <div v-else class="dma-empty mb-3">
            {{ $t('esgPlatform.assessmentFarmer.noDetailsAvailable') || 'No details available' }}
          </div>

          <div
            v-if="moduleName === 'Farms' && row.deforestationStatus && row.deforestationStatus.deforestationStatus"
            class="dma-defor text-center mt-2 mb-2"
          >
            <v-divider class="mb-3" />
            <div class="text-caption font-weight-bold mb-2">
              {{ $t('esgPlatform.assessmentFarmer.overallDeforestationProbability') || 'Overall Deforestation Probability' }}
            </div>
            <div class="dma-status-pill mb-3 mx-auto" :class="getStatusClass(row.deforestationStatus.deforestationStatus)">
              {{ row.deforestationStatus.deforestationStatus }}
            </div>
            <div class="text-caption" style="color: #616161;">
              {{ $t('esgPlatform.assessmentFarmer.reportDate') || 'Report Date' }}:
              <span class="font-weight-bold" style="color: #212121;">{{ formatDateShort(row.deforestationStatus.created_at) }}</span>
            </div>
          </div>

          <div v-if="getDimitraHiddenFieldCount(moduleName, row, rowIdx) > 0" class="dma-more mt-3">
            {{ hiddenCountLabel(getDimitraHiddenFieldCount(moduleName, row, rowIdx)) }}
          </div>

          <div class="mt-auto pt-3">
            <v-btn
              v-if="shouldShowDimitraDetailsButton(moduleName, row)"
              outlined
              class="dma-detail-btn"
              @click="openDetails(moduleName, row, rowIdx)"
            >
              {{ $t('esgPlatform.viewDetails') || 'View Details' }}
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <dimitra-module-detail-dialog v-model="detailDialog" :payload="detailPayload" />
  </div>
</template>

<script>
import moment from "moment";
import { DIMITRA_CONFIG_TEMPLATE } from "../constants/esgDimitraModule.js";
import DimitraModuleDetailDialog from "./DimitraModuleDetailDialog.vue";

const IGNORED_MODULE_KEYS = new Set([
  "app", "modules", "fieldsByModule", "selectionAction",
  "expanded", "progressMetrics", "score", "activeModuleIndex",
]);

export default {
  name: "DimitraModuleAnswer",
  components: { "dimitra-module-detail-dialog": DimitraModuleDetailDialog },
  props: {
    // Enriched shape from the backend: { ModuleName: { rows: [ { fieldKey: val, …, id } ] } }
    dimitraModule: { type: Object, default: () => ({}) },
  },
  data() {
    return { detailDialog: false, detailPayload: null };
  },
  computed: {
    // Normalize to { ModuleName: [rowObj, …] }.
    moduleEntries() {
      const src = this.dimitraModule || {};
      const out = {};
      Object.keys(src).forEach((key) => {
        if (IGNORED_MODULE_KEYS.has(key)) return;
        const val = src[key];
        // Only the enriched shape { rows: [...] } is renderable; a bare id array
        // means the answer wasn't enriched (non-farmer) — render nothing.
        const rows = Array.isArray(val && val.rows) ? val.rows : [];
        if (rows.length) out[key] = rows;
      });
      return out;
    },
    hasRows() {
      return Object.keys(this.moduleEntries).length > 0;
    },
  },
  methods: {
    getDimitraModuleCatalog(moduleName) {
      return (DIMITRA_CONFIG_TEMPLATE.dimitraModuleCatalog && DIMITRA_CONFIG_TEMPLATE.dimitraModuleCatalog[moduleName]) || [];
    },
    getDimitraModuleDisplayName(key) {
      return key === "Soil" ? "Soil Management" : key;
    },
    formatDimitraFieldLabel(key, moduleName = "") {
      const catalogField = this.getDimitraModuleCatalog(moduleName).find((f) => f.key === key);
      if (catalogField && catalogField.label) return catalogField.label;
      if (key === "id") return "ID";
      return String(key || "").replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatDimitraFieldValue(value, meta = {}) {
      if (value === null || value === undefined || value === "") return "-";
      const metaKey = String((meta && meta.key) || "").trim().toLowerCase();

      if (metaKey === "location" && typeof value === "object" && value) {
        const lat = parseFloat(value.lat != null ? value.lat : value.latitude);
        const lng = parseFloat(value.log != null ? value.log : (value.lng != null ? value.lng : value.longitude));
        if (Number.isFinite(lat) && Number.isFinite(lng)) return "Point";
      }
      if (metaKey === "farmgeofence" && Array.isArray(value)) {
        const coords = value
          .map((c) => {
            const lat = parseFloat(c.lat != null ? c.lat : c.latitude);
            const lng = parseFloat(c.log != null ? c.log : (c.lng != null ? c.lng : c.longitude));
            return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
          })
          .filter(Boolean);
        return coords.length >= 3 ? "Polygon" : "-";
      }
      if (Array.isArray(value)) {
        const formatted = value.map((item) => this.formatDimitraFieldValue(item, meta)).filter((i) => i && i !== "-");
        return formatted.length ? formatted.join(", ") : "-";
      }
      if (typeof value === "object") return JSON.stringify(value);
      if (meta && meta.valueType === "date" && moment(value, moment.ISO_8601, true).isValid()) {
        return moment(value).format("DD-MMM-YYYY");
      }
      const formattedValue = String(value);
      return meta && meta.unit ? `${formattedValue} ${meta.unit}` : formattedValue;
    },
    getDimitraRowDisplayFields(moduleName, row = {}) {
      const catalog = this.getDimitraModuleCatalog(moduleName);
      const catalogKeys = catalog.map((f) => f.key);
      const orderedFields = catalog
        .filter((f) => row[f.key] !== undefined)
        .map((f) => {
          let label = f.label;
          if (moduleName === "Soil Management" && f.key === "farmId") label = "Farm Name";
          return { key: f.key, label, value: this.formatDimitraFieldValue(row[f.key], { ...f, key: f.key }), rawValue: row[f.key] };
        });
      const extraFields = Object.keys(row)
        .filter((k) => !catalogKeys.includes(k) && k !== "deforestationStatus" && k !== "id")
        .map((k) => {
          let label = this.formatDimitraFieldLabel(k, moduleName);
          if (moduleName === "Soil Management" && k === "farmId") label = "Farm Name";
          return { key: k, label, value: this.formatDimitraFieldValue(row[k], { key: k }), rawValue: row[k] };
        });
      return [...orderedFields, ...extraFields];
    },
    isMeaningfulDimitraField(field) {
      return !!(field && field.value !== undefined && field.value !== null && field.value !== "" && field.value !== "-");
    },
    getMeaningfulDimitraFields(moduleName, row = {}) {
      return this.getDimitraRowDisplayFields(moduleName, row).filter((f) => this.isMeaningfulDimitraField(f));
    },
    getCompactDimitraValue(value) {
      const normalized = Array.isArray(value) ? value.join(", ") : String(value == null ? "" : value);
      const compact = normalized.split(",")[0].trim();
      return compact.length > 42 ? `${compact.slice(0, 39)}...` : compact || "-";
    },
    getDimitraCardHeading(moduleName, row = {}, rowIdx = 0) {
      const fieldMap = new Map(this.getMeaningfulDimitraFields(moduleName, row).map((f) => [f.key, f]));
      const candidateKeys = ["irrigation_farm", "farm", "farmName", "name", "title", "harvest_cropType", "irrigation_cropType", "farmId", "id"];
      for (const key of candidateKeys) {
        const field = fieldMap.get(key);
        if (field) return { key, title: this.getCompactDimitraValue(field.value) };
      }
      const firstField = this.getMeaningfulDimitraFields(moduleName, row)[0];
      if (firstField) return { key: firstField.key, title: this.getCompactDimitraValue(firstField.value) };
      return { key: null, title: `${moduleName} #${(row && row.id) || rowIdx + 1}` };
    },
    getDimitraCardTitle(moduleName, row = {}, rowIdx = 0) {
      return this.getDimitraCardHeading(moduleName, row, rowIdx).title;
    },
    getDimitraPreviewFields(moduleName, row = {}, rowIdx = 0) {
      const fields = this.getMeaningfulDimitraFields(moduleName, row);
      if (fields.length <= 2) return fields.slice(0, 2);
      const headingKey = this.getDimitraCardHeading(moduleName, row, rowIdx).key;
      const filtered = fields.filter((f) => f.key !== headingKey);
      const limit = moduleName === "Soil Management" ? 4 : 2;
      return (filtered.length ? filtered : fields).slice(0, limit);
    },
    getDimitraHiddenFieldCount(moduleName, row = {}, rowIdx = 0) {
      const fields = this.getMeaningfulDimitraFields(moduleName, row);
      if (fields.length <= 2) return 0;
      const previewKeys = new Set(this.getDimitraPreviewFields(moduleName, row, rowIdx).map((f) => f.key));
      return fields.filter((f) => !previewKeys.has(f.key)).length;
    },
    hiddenCountLabel(count) {
      const t = this.$t("esgPlatform.assessmentFarmer.moreDetailsAvailable", { count });
      if (t && t !== "esgPlatform.assessmentFarmer.moreDetailsAvailable") return t;
      return `+${count} more details available`;
    },
    shouldShowDimitraDetailsButton(moduleName, row = {}) {
      return this.getMeaningfulDimitraFields(moduleName, row).length > 2;
    },
    openDetails(moduleName, row = {}, rowIdx = 0) {
      this.detailPayload = {
        moduleName,
        title: this.getDimitraCardTitle(moduleName, row, rowIdx),
        fields: this.getMeaningfulDimitraFields(moduleName, row),
      };
      this.detailDialog = true;
    },
    formatDateShort(d) {
      return d ? moment(d).format("DD-MM-YYYY") : "-";
    },
    getStatusClass(status) {
      const map = {
        "Very High Deforestation Probability": "v-high-prob",
        "High Deforestation Probability": "high-prob",
        "Medium Deforestation Probability": "medium-prob",
        "Low Deforestation Probability": "low-prob",
        "Very Low Deforestation Probability": "v-low-prob",
        "Zero/Negligible Deforestation Probability": "zero-prob",
        "Manually Mitigated": "manual-prob",
      };
      return map[status] || "";
    },
  },
};
</script>

<style scoped>
.dma-empty { color: #6a756d; font-size: 13px; font-style: italic; }
.dma-module-heading { color: #212121; font-size: 12px; font-weight: 700; letter-spacing: 0.02em; text-transform: uppercase; }
.dma-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; }
.dma-card {
  height: 100%; border: 1px solid #e7ece8; border-radius: 16px; background: #ffffff;
  box-shadow: 0 2px 10px 0 rgba(80, 80, 80, 0.16);
}
.dma-card-title { color: #212121; font-size: 18px; font-weight: 700; line-height: 1.35; min-height: 44px; }
.dma-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px 18px; }
.dma-field { min-width: 0; }
.dma-field-label { color: #7b857d; font-size: 12px; font-weight: 500; margin-bottom: 4px; }
.dma-field-value { color: #212121; font-size: 15px; font-weight: 600; line-height: 1.45; word-break: break-word; }
.dma-more { border-top: 1px solid #e9efeb; color: #6a756d; font-size: 12px; padding-top: 12px; }
.dma-status-pill {
  display: flex; align-items: center; justify-content: center; min-height: 28px;
  max-width: fit-content; padding: 4px 16px; border-radius: 999px; font-size: 13px; font-weight: 600;
}
.v-high-prob { border: 1px solid #8b0000; background: #ff8f8f; color: #8b0000; }
.high-prob { border: 1px solid #8b0000; background: #ffb6b6; color: #8b0000; }
.medium-prob { border: 1px solid #f06b20; background: #ffceb3; color: #f06b20; }
.low-prob { border: 1px solid #f06b20; background: #ffe8c5; color: #f06b20; }
.v-low-prob { border: 1px solid #f49238; background: #fffcec; color: #f49238; }
.zero-prob { border: 1px solid #00bd73; background: #e2fff4; color: #00bd73; }
.manual-prob { border: 1px solid #00bd73; background: #e2fff4; color: #00bd73; }
.dma-detail-btn {
  border-color: #00bd73 !important; color: #00bd73 !important; border-radius: 8px;
  font-size: 12px; font-weight: 700; letter-spacing: 0.04em; min-width: 132px;
}
.dma-detail-btn:hover { background-color: #f2fdf8; }
</style>
